import { useEffect, useRef, useState } from "react";
import { AlertCircle, Eye, EyeOff } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { AuthFrame } from "@/features/auth/AuthFrame";
import { GoogleSignIn, NewWorkspaceLine, useAuthOptions } from "@/features/auth/GoogleSignIn";
import { googleSignInError } from "@/features/signup/signupModel";
import { apiFetch } from "@/lib/api";
import { signInError } from "@/lib/signInError";
import { useNativeSignedOut } from "@/native/useNativeSignedOut";
import { useAuth } from "@/stores/auth";

// Sign-in page, shared by the desktop and the phone (App.tsx renders it
// for every signed-out route). One card on larger screens, full-width
// fields on a phone. The button is always enabled: empty fields are
// reported inline after a submit, with focus on the first one to fix.
// There is no password-reset endpoint, so the help line points to whoever
// set up the account (Corvus for an operator, the parent for a child).
//
// Auth is backend-verified: POST /api/auth/login checks the password
// (bcrypt, users.json in the host-only secrets dir) and sets an HttpOnly
// signed session cookie; requireTenantAccess then rejects any
// cross-tenant API call. The auth store rehydrates from GET /api/auth/me
// on boot, so a refresh keeps the session. Inside the iPhone app, a 401 or
// a sign-out also tells the app the web view is signed out (useNativeSignedOut).
// Google and Create a workspace: features/auth/GoogleSignIn; failures: lib/signInError and /#google=<code>.

interface LoginResponse {
  user: string;
  customerId: string | null;
  role: "customer" | "admin" | "family";
  /** Family (child) logins only: the one agent the session is scoped to. */
  agent: string | null;
}

type FieldErrors = { username?: string; password?: string };

export function Login() {
  useNativeSignedOut();
  const login = useAuth((s) => s.login);
  const sessionEnded = useAuth((s) => s.sessionEnded);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState("");
  const [googleError] = useState(() => googleSignInError(window.location.hash));
  const [loading, setLoading] = useState(false);
  const userRef = useRef<HTMLInputElement>(null);
  const passRef = useRef<HTMLInputElement>(null);
  const offers = useAuthOptions();

  useEffect(() => {
    if (/^#google=/.test(window.location.hash)) window.history.replaceState(null, "", window.location.pathname);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    const missing: FieldErrors = {};
    if (!username.trim()) missing.username = "Enter your username";
    if (!password) missing.password = "Enter your password";
    setFieldErrors(missing);
    if (missing.username || missing.password) {
      (missing.username ? userRef : passRef).current?.focus();
      return;
    }

    setLoading(true);
    try {
      const res = await apiFetch<LoginResponse>("/api/auth/login", {
        method: "POST",
        body: { username: username.trim(), password },
      });
      login(res.user, res.customerId, res.role, res.agent ?? null);
    } catch (e: unknown) {
      setError(signInError(e));
      setLoading(false);
    }
  }

  const onPasswordKey = (e: React.KeyboardEvent<HTMLInputElement>) => setCapsLock(e.getModifierState("CapsLock"));

  return (
    <AuthFrame title="Sign in">
      {sessionEnded ? (
        <p role="status" className="mt-2 text-sm text-fg-muted">
          Your session ended. Sign in again to carry on.
        </p>
      ) : /^\/agent-dev\/open\/app\/?$/.test(window.location.pathname) ? (
        <p className="mt-1 text-base text-fg-muted">Sign in to open the app.</p>
      ) : (
        !offers.google && <p className="mt-1 text-base text-fg-muted">Use the username and password you were given.</p>
      )}

      <form onSubmit={handleSubmit} noValidate className="mt-6 flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="username" className="text-sm font-medium text-fg">
            Username
          </label>
          <Input
            ref={userRef}
            id="username"
            type="text"
            fieldSize="lg"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            autoFocus
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (fieldErrors.username) setFieldErrors((f) => ({ ...f, username: undefined }));
            }}
            disabled={loading}
            aria-invalid={!!fieldErrors.username}
            aria-describedby={fieldErrors.username ? "username-error" : undefined}
          />
          {fieldErrors.username && (
            <p id="username-error" className="text-sm text-danger">
              {fieldErrors.username}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="password" className="text-sm font-medium text-fg">
            Password
          </label>
          <div className="relative">
            <Input
              ref={passRef}
              id="password"
              type={showPassword ? "text" : "password"}
              fieldSize="lg"
              autoComplete="current-password"
              enterKeyHint="go"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (fieldErrors.password) setFieldErrors((f) => ({ ...f, password: undefined }));
              }}
              onKeyDown={onPasswordKey}
              onKeyUp={onPasswordKey}
              onBlur={() => setCapsLock(false)}
              disabled={loading}
              aria-invalid={!!fieldErrors.password}
              aria-describedby={
                [fieldErrors.password && "password-error", capsLock && "caps-lock"].filter(Boolean).join(" ") || undefined
              }
              className="pr-12"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-1 top-1/2 -translate-y-1/2"
            >
              {showPassword ? <EyeOff aria-hidden className="h-4 w-4" /> : <Eye aria-hidden className="h-4 w-4" />}
            </Button>
          </div>
          {fieldErrors.password && (
            <p id="password-error" className="text-sm text-danger">
              {fieldErrors.password}
            </p>
          )}
          {capsLock && (
            <p id="caps-lock" className="text-sm text-warning">
              Caps Lock is on.
            </p>
          )}
        </div>

        <Button type="submit" size="lg" loading={loading} className="mt-2 w-full">
          {loading ? "Signing in…" : "Sign in"}
        </Button>

        {(error || (!offers.google && googleError)) && (
          <p role="alert" className="flex items-start gap-2 text-sm text-danger [text-wrap:pretty]">
            <AlertCircle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
            {error || googleError}
          </p>
        )}
      </form>

      {offers.google && <GoogleSignIn error={googleError} />}
      {offers.signup ? <NewWorkspaceLine /> : (
        <p className="mt-6 text-center text-sm text-fg-muted [text-wrap:balance]">Trouble signing in? Ask whoever set up your Corvus account.</p>
      )}
    </AuthFrame>
  );
}
